import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import theme from "../../themes";
import { pageShellCss } from "../../styles/responsive";
import { Card, Button, Tag, Spin, App, Tooltip, Select } from "antd";
import {
  SendOutlined,
  PaperClipOutlined,
  UserOutlined,
  ClockCircleOutlined,
  LoadingOutlined,
  CloseOutlined,
  LeftOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { Input } from "antd";
import { useAppDispatch, useAppSelector } from "../../store";
import {
  getMailConversationByIdThunk,
  sendMailMessageThunk,
  sendMailFilesThunk,
  updateMailConversationStatusThunk,
} from "../../store/actions/mail.action";
import { clearCurrentConversation } from "../../store/reducers/mail.reducer";
import { IMailMessage } from "../../models/mail.model";
import ToastMessage from "../../utils/toastMessage";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/checkCircle.svg";
import {
  WHATSAPP_ALLOWED_FILE_TYPES,
  MAX_FILE_SIZE,
  getFileIcon,
  formatFileSize,
  isAllowedWhatsappFile,
} from "../../utils/fileUtils";
import { useThreadPolling } from "../../hooks/useThreadPolling";
import { ThreadMedia } from "../../components/ThreadMedia";

const { TextArea } = Input;

const CONVERSATION_STATUS = {
  OPEN: "open",
  NEED_ASSIGN: "need_assign",
  ESCALATED: "need_assign",
  FAILED: "failed",
  COMPLETED: "completed",
};

const statusColors: Record<string, { text: string; background: string }> = {
  open: {
    text: theme.colors.blue1,
    background: theme.colors.blue2,
  },
  Open: {
    text: theme.colors.blue1,
    background: theme.colors.blue2,
  },
  need_assign: {
    text: "#E65100",
    background: "#FFF3E0",
  },
  escalated: {
    text: "#E65100",
    background: "#FFF3E0",
  },
  Assigned: {
    text: "#E65100",
    background: "#FFF3E0",
  },
  failed: {
    text: theme.colors.red1,
    background: "#FFEBEE",
  },
  Failed: {
    text: theme.colors.red1,
    background: "#FFEBEE",
  },
  completed: {
    text: theme.colors.green1,
    background: theme.colors.green2,
  },
  Closed: {
    text: theme.colors.green1,
    background: theme.colors.green2,
  },
  Resolved: {
    text: theme.colors.green1,
    background: theme.colors.green2,
  },
};

// Helper function to format datetime to friendly format
const formatMessageTime = (isoString: string): string => {
  const date = new Date(isoString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const messageDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  );

  const timeStr = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  if (messageDate.getTime() === today.getTime()) {
    return `Today ${timeStr}`;
  } else if (messageDate.getTime() === yesterday.getTime()) {
    return `Yesterday ${timeStr}`;
  } else {
    const diffDays = Math.floor(
      (today.getTime() - messageDate.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (diffDays < 7) {
      const dayName = date.toLocaleDateString("en-US", { weekday: "long" });
      return `${dayName} ${timeStr}`;
    } else {
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    }
  }
};

const MailDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [replyMessage, setReplyMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<Map<string, string>>(new Map());
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const prevMessageCountRef = useRef<number>(0);
  const isInitialLoadRef = useRef<boolean>(true);
  const { message } = App.useApp();
  const toast = ToastMessage(message);

  const { currentConversation, loading } = useAppSelector(
    (state) => state.mail
  );

  // Initial fetch
  useEffect(() => {
    // Reset refs for new conversation
    isInitialLoadRef.current = true;
    prevMessageCountRef.current = 0;

    if (id) {
      dispatch(getMailConversationByIdThunk(id));
    }

    return () => {
      dispatch(clearCurrentConversation());
    };
  }, [id, dispatch]);

  // Polls only while the tab is visible, and backs off on an idle thread. The
  // previous bare 3s interval also restarted itself whenever `status` changed.
  useThreadPolling(
    () => {
      if (id) dispatch(getMailConversationByIdThunk(id));
    },
    {
      enabled: Boolean(id),
      activitySignal: currentConversation?.messages?.length,
    }
  );

  const scrollThreadToBottom = (smooth = false) => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTo({
      top: container.scrollHeight,
      behavior: smooth ? "smooth" : "auto",
    });
  };

  useEffect(() => {
    const currentMessageCount = currentConversation?.messages?.length || 0;

    if (isInitialLoadRef.current && currentMessageCount > 0) {
      scrollThreadToBottom(false);
      isInitialLoadRef.current = false;
    } else if (currentMessageCount > prevMessageCountRef.current) {
      scrollThreadToBottom(true);
    }

    prevMessageCountRef.current = currentMessageCount;
  }, [currentConversation?.messages]);

  const handleSendReply = async () => {
    if ((!replyMessage.trim() && selectedFiles.length === 0) || !currentConversation) return;

    setSending(true);
    try {
      // Send text message if there is one
      if (replyMessage.trim()) {
        await dispatch(
          sendMailMessageThunk({
            message: replyMessage.trim(),
            to: currentConversation.user_email,
            from: currentConversation.company_email,
            conversation_id: currentConversation._id,
          })
        ).unwrap();
      }

      // Send files if selected (all at once, backend accepts up to 10)
      if (selectedFiles.length > 0) {
        await dispatch(
          sendMailFilesThunk({
            files: selectedFiles,
            to: currentConversation.user_email,
            from: currentConversation.company_email,
            conversation_id: currentConversation._id,
          })
        ).unwrap();
        clearAllFiles();
      }

      setReplyMessage("");
      toast.success(
        t("inbound.mail.messageSentSuccessfully"),
        5,
        <CheckCircleIcon />
      );

      // Refresh conversation to get the new message
      if (id) {
        dispatch(getMailConversationByIdThunk(id));
      }
    } catch (error) {
      toast.error(t("inbound.mail.failedToSendMessage"), 5);
    } finally {
      setSending(false);
    }
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const newFiles: File[] = [];

    Array.from(files).forEach((file) => {
      // Validate file type
      if (!isAllowedWhatsappFile(file)) {
        toast.error(t("inbound.mail.fileTypeNotSupported"), 5);
        return;
      }

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        toast.error(t("inbound.mail.fileTooLarge"), 5);
        return;
      }

      // Check for duplicates by name
      const fileKey = `${file.name}-${file.size}`;
      if (selectedFiles.some((f) => `${f.name}-${f.size}` === fileKey)) {
        return;
      }

      newFiles.push(file);

      // Create preview for images
      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreviews((prev) => {
            const updated = new Map(prev);
            updated.set(fileKey, reader.result as string);
            return updated;
          });
        };
        reader.readAsDataURL(file);
      }
    });

    if (newFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...newFiles]);
    }

    // Reset input to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeFile = (fileToRemove: File) => {
    const fileKey = `${fileToRemove.name}-${fileToRemove.size}`;
    setSelectedFiles((prev) => prev.filter((f) => `${f.name}-${f.size}` !== fileKey));
    setFilePreviews((prev) => {
      const updated = new Map(prev);
      updated.delete(fileKey);
      return updated;
    });
  };

  const clearAllFiles = () => {
    setSelectedFiles([]);
    setFilePreviews(new Map());
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const getStatusColor = (status: string) => {
    return statusColors[status] || { text: theme.colors.grey1, background: theme.colors.grey5 };
  };

  const getStatusLabel = (status: string) => {
    switch (String(status || "").toLowerCase()) {
      case CONVERSATION_STATUS.OPEN:
        return t("inbound.mail.statusOpen");
      case CONVERSATION_STATUS.NEED_ASSIGN:
      case "escalated":
      case "need_reply":
      case "assigned":
        return t("inbound.whatsapp.needsAssign");
      case CONVERSATION_STATUS.FAILED:
        return t("inbound.mail.statusFailed");
      case CONVERSATION_STATUS.COMPLETED:
      case "closed":
      case "resolved":
        return t("inbound.whatsapp.statusCompleted");
      default:
        return status;
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!currentConversation || !id) return;

    setUpdatingStatus(true);
    try {
      await dispatch(
        updateMailConversationStatusThunk({
          id: currentConversation._id,
          status: newStatus,
        })
      ).unwrap();
      toast.success(t("inbound.mail.statusUpdatedSuccessfully") || "Status updated successfully", 3);
    } catch (error) {
      toast.error(t("inbound.mail.failedToUpdateStatus") || "Failed to update status", 5);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const statusOptions = [
    { value: CONVERSATION_STATUS.OPEN, label: getStatusLabel(CONVERSATION_STATUS.OPEN) },
    { value: CONVERSATION_STATUS.NEED_ASSIGN, label: getStatusLabel(CONVERSATION_STATUS.NEED_ASSIGN) },
    { value: CONVERSATION_STATUS.FAILED, label: getStatusLabel(CONVERSATION_STATUS.FAILED) },
    { value: CONVERSATION_STATUS.COMPLETED, label: getStatusLabel(CONVERSATION_STATUS.COMPLETED) },
  ];

  // Filter out system messages (first assistant message without created_at is usually system prompt)
  const getDisplayMessages = () => {
    if (!currentConversation?.messages) return [];

    return currentConversation.messages.filter((msg) => {
      // Filter out system prompts (assistant messages without created_at)
      if (msg.role === "assistant" && !msg.created_at) {
        return false;
      }
      return true;
    });
  };

  const getSenderName = (msg: IMailMessage) => {
    return msg.role === "user"
      ? currentConversation?.recipient_name || "Customer"
      : "Agent";
  };

  const getSubjectFromMessages = () => {
    const subject = currentConversation?.subject?.trim() || "";
    const summary = currentConversation?.summary?.trim() || "";
    let fallback = "";
    if (!subject && !summary && currentConversation?.messages) {
      const lastUserMessage = [...currentConversation.messages].find(
        (msg) => msg.role === "user"
      );
      fallback = lastUserMessage?.content || "";
    }
    const display =
      subject && summary && subject.length < summary.length
        ? subject
        : summary || subject || fallback;
    if (!display) return <span></span>;
    const tooltip =
      summary && summary !== display
        ? summary
        : display.length > 80
          ? display
          : "";

    return (
      <Tooltip title={tooltip || undefined} placement="topLeft">
        <span>{display}</span>
      </Tooltip>
    );
  };

  if (loading) {
    return (
      <FlexRow>
        <MainContent>
          <LoadingContainer>
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
            />
          </LoadingContainer>
        </MainContent>
      </FlexRow>
    );
  }

  if (!currentConversation) {
    return (
      <FlexRow>
        <MainContent>
          <BackButton onClick={() => navigate("/inbound/mail")}>
            <LeftOutlined style={{ marginRight: 8 }} />
            {t("inbound.mail.backToList")}
          </BackButton>
          <Card>
            <div style={{ textAlign: "center", padding: "40px" }}>
              {t("inbound.mail.conversationNotFound")}
            </div>
          </Card>
        </MainContent>
      </FlexRow>
    );
  }

  const displayMessages = getDisplayMessages();

  return (
    <FlexRow>
      <MainContent>
        <BackButton onClick={() => navigate("/inbound/mail")}>
          <LeftOutlined style={{ marginRight: 8 }} />
          {t("inbound.mail.backToList")}
        </BackButton>

        <DetailCard>
          <ConversationHeader>
            <ConversationTitle>{getSubjectFromMessages()}</ConversationTitle>
            <ConversationMeta>
              <StatusSelectWrapper>
                <StatusSelect
                  value={String(currentConversation.status || "open").toLowerCase() === "assigned"
                    ? CONVERSATION_STATUS.ESCALATED
                    : ["closed", "resolved"].includes(String(currentConversation.status || "").toLowerCase())
                      ? CONVERSATION_STATUS.COMPLETED
                      : String(currentConversation.status || CONVERSATION_STATUS.OPEN).toLowerCase()}
                  onChange={(value) => handleStatusChange(value as string)}
                  loading={updatingStatus}
                  disabled={updatingStatus}
                  size="middle"
                  dropdownStyle={{ minWidth: 160 }}
                >
                  {statusOptions.map((option) => {
                    const colors = getStatusColor(option.value);
                    return (
                      <Select.Option key={option.value} value={option.value}>
                        <StatusOption>
                          <Tag
                            style={{
                              margin: 0,
                              background: colors.background,
                              color: colors.text,
                              border: "none",
                            }}
                          >
                            {option.label}
                          </Tag>
                        </StatusOption>
                      </Select.Option>
                    );
                  })}
                </StatusSelect>
              </StatusSelectWrapper>
              <MetaItem>
                <UserOutlined /> {currentConversation.recipient_name}
              </MetaItem>
              <MetaItem>
                <MailIconSmall /> {currentConversation.user_email || t("common.notAvailable")}
              </MetaItem>
            </ConversationMeta>
          </ConversationHeader>

          <MessagesContainer ref={messagesContainerRef}>
            <MessagesWrapper>
              {displayMessages.map((msg, index) => {
                const isAgent = msg.role === "assistant";
                const senderName = getSenderName(msg);
                const prevMsg = index > 0 ? displayMessages[index - 1] : null;
                const isSameSender = prevMsg?.role === msg.role;

                return (
                  <MessageBubble
                    key={`${msg.role}-${index}`}
                    $isAgent={isAgent}
                    $isSameSender={isSameSender}
                  >
                    {!isSameSender && (
                      <MessageHeader>
                        <SenderAvatar $isAgent={isAgent}>
                          {senderName.charAt(0)}
                        </SenderAvatar>
                        <SenderInfo>
                          <SenderName>{senderName}</SenderName>
                          {msg.created_at && (
                            <MessageTime>
                              <ClockCircleOutlined />{" "}
                              {formatMessageTime(msg.created_at)}
                            </MessageTime>
                          )}
                        </SenderInfo>
                      </MessageHeader>
                    )}
                    <MessageContent $isAgent={isAgent} $isSameSender={isSameSender}>
                      <ThreadMedia
                        messageId={msg._id}
                        items={msg.media}
                        fallbackUrl={msg.media_url}
                      />
                      {msg.content}
                    </MessageContent>
                  </MessageBubble>
                );
              })}
            </MessagesWrapper>
          </MessagesContainer>

          {!["completed", "failed", "closed", "resolved"].includes(
            String(currentConversation.status || "").toLowerCase()
          ) && (
            <ReplySection>
              <HiddenFileInput
                ref={fileInputRef}
                type="file"
                accept={WHATSAPP_ALLOWED_FILE_TYPES.join(",")}
                onChange={handleFileSelect}
                multiple
              />
              {selectedFiles.length > 0 && (
                <FilePreviewList>
                  {selectedFiles.map((file) => {
                    const fileKey = `${file.name}-${file.size}`;
                    const preview = filePreviews.get(fileKey);
                    return (
                      <FilePreviewItem key={fileKey}>
                        <FilePreviewContent>
                          {preview ? (
                            <FilePreviewImage src={preview} alt="Preview" />
                          ) : (
                            <FilePreviewIcon>{getFileIcon(file.type)}</FilePreviewIcon>
                          )}
                          <FilePreviewInfo>
                            <FilePreviewName>{file.name}</FilePreviewName>
                            <FilePreviewSize>
                              {formatFileSize(file.size)}
                            </FilePreviewSize>
                          </FilePreviewInfo>
                        </FilePreviewContent>
                        <FileRemoveButton
                          onClick={() => removeFile(file)}
                          title={t("inbound.mail.removeFile")}
                        >
                          <CloseOutlined />
                        </FileRemoveButton>
                      </FilePreviewItem>
                    );
                  })}
                </FilePreviewList>
              )}
              <ReplyInputContainer>
                <ReplyTextArea
                  placeholder={t("inbound.mail.typeMessage")}
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  autoSize={{ minRows: 3, maxRows: 6 }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendReply();
                    }
                  }}
                />
                <ReplyActions>
                  <ActionButtons>
                    <ActionButton
                      onClick={handleAttachClick}
                      title={t("inbound.mail.attachFile")}
                    >
                      <PaperClipOutlined />
                    </ActionButton>
                  </ActionButtons>
                  <SendButton
                    type="primary"
                    icon={<SendOutlined />}
                    onClick={handleSendReply}
                    disabled={(!replyMessage.trim() && selectedFiles.length === 0) || sending}
                    loading={sending}
                  >
                    {t("inbound.mail.send")}
                  </SendButton>
                </ReplyActions>
              </ReplyInputContainer>
            </ReplySection>
          )}
        </DetailCard>
      </MainContent>
    </FlexRow>
  );
};

// Styled Components
const FlexRow = styled.div`
  display: flex;
  min-height: 100%;
  background-color: ${theme.colors.grey3};
`;

const MainContent = styled.div`
  flex: 1;
  min-width: 0;
  gap: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  ${pageShellCss}

  @media (max-width: 900px) {
    min-height: calc(100dvh - 56px);
    max-height: calc(100dvh - 56px);
  }
`;

const LoadingContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BackButton = styled.div`
  color: ${theme.colors.blue1};
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
`;

const DetailCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${theme.colors.grey2};
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  min-height: 0;

  .ant-card-body {
    padding: 0;
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    min-height: 0;
  }
`;

const ConversationHeader = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid ${theme.colors.grey2};

  @media (max-width: 640px) {
    padding: 16px;
  }
`;

const ConversationTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 12px 0;
  overflow-wrap: anywhere;

  @media (max-width: 640px) {
    font-size: 18px;
  }
`;

const ConversationMeta = styled.div`
  display: flex;
  gap: 12px 20px;
  align-items: center;
  flex-wrap: wrap;
`;

const MetaItem = styled.span`
  font-size: 14px;
  color: ${theme.colors.grey1};
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  background: ${theme.colors.grey4};
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const MessagesWrapper = styled.div`
  margin-top: auto;
`;

const MessageBubble = styled.div<{ $isAgent: boolean; $isSameSender: boolean }>`
  margin-top: ${({ $isSameSender }) => ($isSameSender ? "4px" : "16px")};

  &:first-child {
    margin-top: 0;
  }
`;

const MessageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
`;

const SenderAvatar = styled.div<{ $isAgent: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${({ $isAgent }) =>
    $isAgent ? theme.colors.green1 : theme.colors.blue1};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
`;

const SenderInfo = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const SenderName = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const MessageTime = styled.span`
  font-size: 14px;
  color: ${theme.colors.grey1};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const MessageContent = styled.div<{ $isAgent: boolean; $isSameSender?: boolean }>`
  background: ${({ $isAgent }) => ($isAgent ? "#e8f5e9" : "#fff")};
  padding: 12px 16px;
  border-radius: 12px;
  margin-left: 48px;
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  white-space: pre-wrap;
  width: fit-content;
  max-width: 70%;
`;

const ReplySection = styled.div`
  border-top: 1px solid ${theme.colors.grey2};
  padding: 16px 24px;
  background: #fff;
`;

const ReplyInputContainer = styled.div`
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  overflow: hidden;
`;

const ReplyTextArea = styled(TextArea)`
  border: none;
  resize: none;
  padding: 12px 16px;

  &:focus,
  &:hover {
    border: none;
    box-shadow: none;
  }
`;

const ReplyActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: ${theme.colors.grey4};
  border-top: 1px solid ${theme.colors.grey2};
  gap: 8px;
  flex-wrap: wrap;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  color: ${theme.colors.grey1};
  font-size: 18px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: ${theme.colors.grey2};
    color: #333;
  }
`;

const SendButton = styled(Button)`
  background: ${theme.colors.green1};
  border-color: ${theme.colors.green1};

  &:hover {
    background: ${theme.colors.green1} !important;
    border-color: ${theme.colors.green1} !important;
    opacity: 0.9;
  }
`;

const MailIconSmall = styled(MailOutlined)`
  font-size: 16px;
  color: ${theme.colors.blue1};
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const FilePreviewList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 16px;
  background: ${theme.colors.grey4};
  max-height: 200px;
  overflow-y: auto;
`;

const FilePreviewItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid ${theme.colors.grey2};
  border-radius: 8px;
  max-width: 280px;
`;

const FilePreviewContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const FilePreviewImage = styled.img`
  width: 36px;
  height: 36px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid ${theme.colors.grey2};
  flex-shrink: 0;
`;

const FilePreviewIcon = styled.div`
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.grey2};
  border-radius: 4px;
  font-size: 18px;
  color: ${theme.colors.grey1};
  flex-shrink: 0;
`;

const FilePreviewInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FilePreviewName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FilePreviewSize = styled.div`
  font-size: 12px;
  color: ${theme.colors.grey1};
  margin-top: 2px;
`;

const FileRemoveButton = styled.button`
  background: ${theme.colors.grey2};
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${theme.colors.grey1};
  font-size: 12px;
  border-radius: 50%;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;

  &:hover {
    background: #ff4d4f;
    color: #fff;
  }
`;

const StatusSelectWrapper = styled.div`
  position: relative;
  display: inline-block;
  cursor: pointer;
`;

const StatusSelect = styled(Select)`
  width: fit-content !important;
  height: fit-content !important;
  
  .ant-select-selector {
    border: none !important;
    box-shadow: none !important;
    background: transparent !important;
    width: fit-content !important;
    height: fit-content !important;
    min-height: auto !important;
    padding: 0 !important;
  }
  
  .ant-select-selection-item {
    padding: 0 !important;
    line-height: 1 !important;
    height: auto !important;
  }
  
  .ant-select-arrow {
    display: none !important;
  }
  
  &:hover .ant-select-selector,
  &.ant-select-focused .ant-select-selector {
    border: none !important;
    box-shadow: none !important;
  }
`;

const StatusOption = styled.div`
  display: flex;
  align-items: center;
`;

export default MailDetail;
